import { types as api, types } from '@mesa-labs/mesa-api';
import { VendorApi } from '@mesa-labs/mesa-api/dist/apis/vendor';
import { InvoiceApi } from '@mesa-labs/mesa-api/dist/apis/invoice';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { ProgramCodes } from '@mesa-labs/mesa-api/dist/types';
import { appendFilterParams } from '../../utils';
import {
  getIdToken,
  prepareHeaders,
  StatisticsMutex,
  useApi,
} from './api';
import { hardcodedVendorUnderwriting, hardcodedUnderwritingDashboard } from './devHardcodedResponses';
import { hardcodedTimeSeries } from './devHardcodedTimeSeries';
import { hardcodedCashFlowCategories } from './devHardcodedCashFlowCategories';
import { hardcodedCashFlowSubcategories } from './devHardcodedCashFlowSubcategories';

const isDev = process.env.NODE_ENV === 'development';

export const vendorsApi = createApi({
  reducerPath: 'vendorsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CONFIG.api.vendorUrl,
    prepareHeaders,
  }),
  tagTypes: ['Vendors', 'Vendor', 'Vendor Documents', 'Vendor Notes', 'Vendor Audit',
    'Program', 'Programs', 'User Accounts', 'Onboardings', 'Onboarding', 'Third Party Business Verifications',
    'Facilitators', 'FacilitatorTotals', 'Vendor Service Agreements', 'Vendor Partner Service Agreements',
    'TaskSchedules', 'TaskExecutions', 'Vendor Surveys', 'Vendor User Account Activity Metrics',
    'Vendor Underwriting', 'Vendor Underwriting Assessments',
    'Vendor Account Underwriting Balance', 'Vendor Account Underwriting Transactions', 'Vendor Account Underwriting Transaction Matched Invoices', 'Vendor Matching Suggestions',
    'Underwriting Transaction Labelling Details',
    'Vendor Account Underwriting Dashboard',
    'Vendor Account Underwriting Cash Flow Categories',
    'Vendor Account Underwriting Cash Flow Subcategories',
    'ConfigurationSettings',
    'Vendor UCC Filings',
    'Vendor Sub Accounts',
    'All Vendor Sub Accounts',
    'Vendor Program Promotions',
    'Program Promotions',
    'Vendors In Enablement V2',
    'Vendor Program Portal Configurations',
    'Vendor Limit Audit Log',
    'Vendor Email Sequences',
    'Vendor Action Items',
    'Vendor Action Item Timeline',
    'All Vendor Action Items',
    'Vendor Action Item Assignees',
  ],
  endpoints: (build) => ({
    getAllVendors:
      build.query<api.IPagedResults<api.VendorResponse>, api.VendorFilterParams>({
        async queryFn(params) {
          const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
          const data = await externalVendorApi.getAllVendors<api.VendorResponse>(params);
          return { data };
        },
        providesTags: ['Vendors'],
      }),
    getVendor: build.query<api.VendorAggregateResponse | undefined, { id: string }>({
      async queryFn({ id }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendor<api.VendorAggregateResponse>(id);
        return { data };
      },
      providesTags: (result, error, id) => (result
        ? [{ type: 'Vendor', ...id, ...result }]
        : ['Vendor']),
    }),
    impersonateVendor: build.mutation<void, { id: string }>({
      async queryFn({ id }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.impersonateVendor(id);
        return { data };
      },
    }),
    endVendorImpersonation: build.mutation<void, { id: string }>({
      async queryFn({ id }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.endVendorImpersonation(id);
        return { data };
      },
    }),
    endPartnerImpersonation: build.mutation<void, { partnerId: api.Partners }>({
      async queryFn({ partnerId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.endPartnerImpersonation(partnerId);
        return { data };
      },
    }),
    updateVendor: build.mutation<api.VendorResponse | undefined, api.UpdateVendorRequest & { id: string }>({
      async queryFn({ id, ...body }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.updateVendor<api.VendorResponse>(id, body);
        return { data };
      },
      invalidatesTags: (result: any) => ['Vendors', { type: 'Vendor', id: result.id }, 'Vendor Audit'],
    }),
    patchVendor: build.mutation<api.VendorResponse | undefined, api.PatchVendorRequest & { id: string }>({
      async queryFn({ id, ...body }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.patchVendor<api.VendorResponse>(id, body);
        return { data };
      },
      invalidatesTags: (result: any) => ['Vendors', { type: 'Vendor', id: result.id }, 'Vendor Audit', 'Vendors In Enablement V2'],
    }),
    assignVendor: build.mutation<api.VendorResponse | undefined, api.AssignVendorRequest & { id: types.UUID }>({
      async queryFn({ id, ...body }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.assignVendor<api.VendorResponse>(id, body);
        return { data };
      },
      invalidatesTags: (result: any) => ['Vendors', { type: 'Vendor', id: result.id }, 'Vendor Audit'],
    }),
    updateVendorAssignments: build.mutation<api.VendorResponse | undefined, api.UpdateVendorAssignmentsRequest & { id: types.UUID }>({
      async queryFn({ id, ...body }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.updateVendorAssignments<api.VendorResponse>(id, body);
        return { data };
      },
      invalidatesTags: (result: any) => ['Vendors', { type: 'Vendor', id: result.id }, 'Vendor Audit'],
    }),
    activateVendor: build.mutation<api.VendorResponse | undefined, api.ActivateVendorRequest & { id: types.UUID }>({
      async queryFn({ id, ...body }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.activateVendor<api.VendorResponse>(id, body);
        return { data };
      },
      invalidatesTags: (result: any) => ['Vendors', { type: 'Vendor', id: result.id }, 'Vendor Audit'],
    }),
    deactivateVendor: build.mutation<api.VendorResponse | undefined, api.DeactivateVendorRequest & { id: api.UUID }>({
      async queryFn({ id, deactivatedReason }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.deactivateVendor<api.VendorResponse>(id, { deactivatedReason });
        return { data };
      },
      invalidatesTags: (result: any) => ['Vendors', { type: 'Vendor', id: result.id }, 'Vendor Audit'],
    }),
    markVendorDormant: build.mutation<api.VendorResponse | undefined, api.MarkDormantVendorRequest & { id: api.UUID }>({
      async queryFn({ id, dormantReasons, dormancyExplanation, underwritingNote }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.markVendorDormant<api.VendorResponse>(id, { dormantReasons, dormancyExplanation, underwritingNote });
        return { data };
      },
      invalidatesTags: (result: any) => ['Vendors', { type: 'Vendor', id: result.id }, 'Vendor Audit', { type: 'Vendor Notes' as const, id: result.id }],
    }),
    sendPaymentScheduleEmail: build.mutation<api.VendorResponse | undefined, { id: api.UUID }>({
      async queryFn({ id }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.sendPaymentScheduleEmail<api.VendorResponse>(id);
        return { data };
      },
      invalidatesTags: (result: any) => ['Vendors', { type: 'Vendor', id: result.id }, 'Vendor Audit'],
    }),
    createProgram: build.mutation<api.VendorProgramResponse, api.VendorProgramRequest & { vendorId: api.UUID; productCode: api.ProductCodes }>({
      async queryFn({ vendorId, productCode, ...body }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.createVendorProgram<api.VendorProgramResponse>(vendorId, productCode, body);
        return { data };
      },
      invalidatesTags: (result: any) => ['Vendors', { type: 'Vendor', id: result.vendorId }, 'Vendor Audit'],
    }),
    patchVendorProgram: build.mutation<api.VendorProgramResponse | undefined, api.PatchVendorProgramRequest & { vendorId: api.UUID; productCode: api.ProductCodes; partnerId: api.Partners; minimumFundingDaysToCollectionDate?: number }>({
      async queryFn({ vendorId, productCode, partnerId, ...body }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.patchVendorProgram<api.VendorProgramResponse>(vendorId, productCode, partnerId, body);
        return { data };
      },
      invalidatesTags: (result: any) => ['Vendors', { type: 'Vendor', id: result.vendorId }, 'Vendor Audit'],
    }),
    activateVendorProgram: build.mutation<api.VendorProgramResponse | undefined, & { vendorId: api.UUID; productCode: api.ProductCodes; partnerId: api.Partners }>({
      async queryFn({ vendorId, productCode, partnerId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.activateVendorProgram<api.VendorProgramResponse>(vendorId, productCode, partnerId);
        return { data };
      },
      invalidatesTags: (result: any, error: any, { vendorId }: any) => ['Vendors', { type: 'Vendor', id: vendorId }, 'Vendor Audit', 'Vendor Program Portal Configurations', 'Vendor Program Promotions'],
    }),
    deactivateVendorProgram: build.mutation<api.VendorProgramResponse | undefined, { vendorId: api.UUID; productCode: api.ProductCodes; partnerId: api.Partners }>({
      async queryFn({ vendorId, productCode, partnerId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.deactivateVendorProgram<api.VendorProgramResponse>(vendorId, productCode, partnerId);
        return { data };
      },
      invalidatesTags: (result: any, error: any, { vendorId }: any) => ['Vendors', { type: 'Vendor', id: vendorId }, 'Vendor Audit'],
    }),
    deleteVendorProgram: build.mutation<api.VendorProgramResponse | undefined, { vendorId: api.UUID; productCode: api.ProductCodes; partnerId: api.Partners }>({
      async queryFn({ vendorId, productCode, partnerId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.deleteVendorProgram<api.VendorProgramResponse>(vendorId, productCode, partnerId);
        return { data };
      },
      invalidatesTags: (result: any, error: any, { vendorId }: any) => ['Vendors', { type: 'Vendor', id: vendorId }, 'Vendor Audit', 'Vendor Program Portal Configurations', 'Vendor Program Promotions'],
    }),
    rejectVendorProgramPortalAccess: build.mutation<api.VendorProgramPortalConfigurationResponse | undefined, api.RejectVendorProgramPortalAccessRequest & { vendorId: api.UUID; productCode: api.ProductCodes; partnerId: api.Partners }>({
      async queryFn({ vendorId, productCode, partnerId, ...body }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.rejectVendorProgramPortalAccess(vendorId, productCode, partnerId, body);
        return { data };
      },
      invalidatesTags: (result: any, error: any, { vendorId }: any) => ['Vendors', { type: 'Vendor', id: vendorId }, 'Vendor Audit', 'Vendor Program Portal Configurations'],
    }),
    offerVendorProgramPricing: build.mutation<api.VendorProgramResponse | undefined, { vendorId: api.UUID; productCode: api.ProductCodes; partnerId: api.Partners }>({
      async queryFn({ vendorId, productCode, partnerId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.offerVendorProgramPricing(vendorId, productCode, partnerId);
        return { data };
      },
      invalidatesTags: (result: any, error: any, { vendorId }: any) => ['Vendors', { type: 'Vendor', id: vendorId }, 'Vendor Audit'],
    }),
    getVendorProgramPortalConfigurations: build.query<readonly api.VendorProgramPortalConfigurationResponse[], api.UUID>({
      async queryFn(vendorId) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorProgramPortalConfigurations(vendorId);
        return { data };
      },
      providesTags: ['Vendor Program Portal Configurations'],
    }),
    createFactoringVendorProgramPromotion: build.mutation<api.VendorProgramPromotionResponse | undefined, api.CreateFactoringVendorProgramPromotionRequest>({
      async queryFn(body) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.createFactoringVendorProgramPromotion(body);
        return { data };
      },
      invalidatesTags: (result: any) => ['Vendors', { type: 'Vendor', id: result?.vendorId }, 'Vendor Audit', 'Vendor Program Promotions'],
    }),
    createFactoringVendorProgramPromotions: build.mutation<api.CreateFactoringVendorProgramPromotionsResponse, api.CreateFactoringVendorProgramPromotionsRequest>({
      async queryFn(body) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.createFactoringVendorProgramPromotions(body);
        return { data };
      },
      invalidatesTags: ['Vendors', 'Vendor Audit', 'Vendor Program Promotions'],
    }),
    createProgramOverride: build.mutation<api.VendorProgramOverrideResponse | undefined, api.VendorProgramOverrideRequest & { vendorId: api.UUID }>({
      async queryFn({ vendorId, ...body }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.createVendorProgramOverride<api.VendorProgramOverrideResponse>(vendorId, body);
        return { data };
      },
      invalidatesTags: (result: any) => ['Vendors', { type: 'Vendor', id: result.vendorId }, 'Vendor Audit'],
    }),
    patchProgramOverride: build.mutation<api.VendorProgramOverrideResponse | undefined, api.PatchVendorProgramOverrideRequest & { vendorId: api.UUID; id: api.UUID }>({
      async queryFn({ vendorId, id, ...body }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.patchVendorProgramOverride<api.VendorProgramOverrideResponse>(vendorId, id, body);
        return { data };
      },
      invalidatesTags: (result: any) => ['Vendors', { type: 'Vendor', id: result.vendorId }, 'Vendor Audit'],
    }),
    addVendorAccount: build.mutation<api.VendorAccountAggregateResponse, api.VendorAccountRequest & { id: api.UUID }>({
      async queryFn({ id, ...body }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.createVendorAccount<api.VendorAccountAggregateResponse>(id, body);
        return { data };
      },
      invalidatesTags: (_result: any, _error, parameters) => ['Vendors', { type: 'Vendor', id: parameters.id }, 'Vendor Audit'],
    }),
    verifyVendorAccount: build.mutation<api.VendorAccountAggregateResponse | undefined, api.VendorAccountVerificationRequest & { id: api.UUID, accountId: api.UUID }>({
      async queryFn({ id, accountId, ...body }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.verifyVendorAccount<api.VendorAccountAggregateResponse>(id, accountId, body);
        return { data };
      },
      invalidatesTags: (_result: any, _error, parameters) => ['Vendors', { type: 'Vendor', id: parameters.id }, 'Vendor Audit'],
    }),
    makeVendorAccountPrimary: build.mutation<api.VendorAccountAggregateResponse | undefined, { id: api.UUID; accountId: api.UUID }>({
      async queryFn({ id, accountId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.makeVendorAccountPrimary<api.VendorAccountAggregateResponse>(id, accountId);
        return { data };
      },
      invalidatesTags: (_result: any, _error, parameters) => ['Vendors', { type: 'Vendor', id: parameters.id }, 'Vendor Audit'],
    }),
    makeVendorAccountDebitOnly: build.mutation<api.VendorAccountAggregateResponse | undefined, { id: api.UUID; accountId: api.UUID }>({
      async queryFn({ id, accountId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.makeVendorAccountDebitOnly<api.VendorAccountAggregateResponse>(id, accountId);
        return { data };
      },
      invalidatesTags: (_result: any, _error, parameters) => ['Vendors', { type: 'Vendor', id: parameters.id }, 'Vendor Audit'],
    }),
    overrideVendorAccountDirectDebitVerification: build.mutation<api.VendorAccountAggregateResponse | undefined, { id: api.UUID; accountId: api.UUID }>({
      async queryFn({ id, accountId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.overrideVendorAccountDirectDebitVerification<api.VendorAccountAggregateResponse>(id, accountId);
        return { data };
      },
      invalidatesTags: (_result: any, _error, parameters) => ['Vendors', { type: 'Vendor', id: parameters.id }, 'Vendor Audit'],
    }),
    refreshVendorAccountUnderwritingTransactions: build.mutation<void, { id: api.UUID; accountId: api.UUID }>({
      async queryFn({ id, accountId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        await externalVendorApi.refreshVendorAccountUnderwritingTransactions(id, accountId);
        return { data: undefined };
      },
      // No invalidation: refreshed transactions arrive asynchronously via Plaid's webhook and the sync
    }),
    hideSecondaryVendorAccount: build.mutation<api.VendorAccountAggregateResponse | undefined, { id: api.UUID; accountId: api.UUID }>({
      async queryFn({ id, accountId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.hideSecondaryVendorAccount<api.VendorAccountAggregateResponse>(id, accountId);
        return { data };
      },
      invalidatesTags: (_result: any, _error, parameters) => ['Vendors', { type: 'Vendor', id: parameters.id }, 'Vendor Audit'],
    }),
    requestBankDocument: build.mutation<api.RequestBankDocumentResponse | undefined, { id: api.UUID; accountId: api.UUID }>({
      async queryFn({ id, accountId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.requestBankDocument(id, accountId);
        return { data };
      },
    }),
    getUserAccounts: build.query<readonly api.UserAccountResponse[], { id: api.UUID }>({
      async queryFn({ id }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getAllUserAccounts<api.UserAccountResponse>(id);
        return { data };
      },
      providesTags: (result, error, parameters) => (result
        ? [{ type: 'User Accounts', id: parameters.id }]
        : []),
    }),
    getAllAbandonedUserAccounts: build.query<api.IPagedResults<api.UserAccountResponse> | undefined, api.GetAllAbandonedUsersFilterParams>({
      async queryFn(params) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getAllAbandonedUserAccounts<api.UserAccountResponse>(params);
        return { data };
      },
      providesTags: ['User Accounts'],
    }),
    addUserAccount: build.mutation<api.UserAccountResponse, api.UserAccountRequest & { id: api.UUID; }>({
      async queryFn({ id, ...body }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.createChildUserAccount<api.UserAccountResponse>(id, body);
        return { data };
      },
      invalidatesTags: (result, _error, parameters) => (result
        ? [{ type: 'User Accounts', id: parameters.id }]
        : []),
    }),
    resendUserAccountInvite: build.mutation<api.UserAccountResponse | undefined, { id: api.UUID; email: string }>({
      async queryFn({ id, email }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.resendChildUserAccountInvite<api.UserAccountResponse>(id, email);
        return { data };
      },
    }),
    promoteUserAccount: build.mutation<api.UserAccountResponse | undefined, { id: api.UUID; email: string }>({
      async queryFn({ id, email }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.promoteChildUserAccount<api.UserAccountResponse>(id, email);
        return { data };
      },
      invalidatesTags: (result, _error, parameters) => (result
        ? [{ type: 'User Accounts', id: parameters.id }]
        : []),
    }),
    toggleUserAccountReadOnly: build.mutation<api.UserAccountResponse | undefined, { email: string }>({
      async queryFn({ email }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.toggleUserAccountReadOnly<api.UserAccountResponse>(email);
        return { data };
      },
      invalidatesTags: (result) => (result
        ? [{ type: 'User Accounts', id: result.vendorId }]
        : []),
    }),
    deactivateUserAccount: build.mutation<api.UserAccountResponse | undefined, { email: string } & api.DeactivateUserAccountRequest>({
      async queryFn({ email, ...request }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.deactivateUserAccount<api.UserAccountResponse>(email, request);
        return { data };
      },
      invalidatesTags: (result) => (result
        ? [{ type: 'User Accounts', id: result.vendorId }]
        : []),
    }),
    reactivateUserAccount: build.mutation<api.UserAccountResponse | undefined, { email: string }>({
      async queryFn({ email }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.reactivateUserAccount<api.UserAccountResponse>(email);
        return { data };
      },
      invalidatesTags: (result) => (result
        ? [{ type: 'User Accounts', id: result.vendorId }]
        : []),
    }),
    deleteUserAccount: build.mutation<api.UserAccountResponse | undefined, { email: string }>({
      async queryFn({ email }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.deleteUserAccount<api.UserAccountResponse>(email);
        return { data };
      },
      invalidatesTags: (result) => (result
        ? [{ type: 'User Accounts', id: result.vendorId }]
        : []),
    }),
    getVendorUserAccountActivityMetrics: build.query<api.VendorUserAccountActivityMetricsResponse | undefined, { vendorId: api.UUID }>({
      async queryFn({ vendorId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorUserAccountActivityMetrics(vendorId);
        return { data };
      },
      providesTags: (result, error, { vendorId }) => (result
        ? [{ type: 'Vendor User Account Activity Metrics', vendorId }]
        : ['Vendor User Account Activity Metrics']),
    }),
    getVendorActivityMetrics: build.query<api.IPagedResults<api.VendorUserAccountActivityMetricsResponse>, api.VendorActivityMetricsFilterParams>({
      async queryFn(params) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorActivityMetrics(params);
        return { data };
      },
      providesTags: (result, error, { vendorId }) => (result
        ? [{ type: 'Vendor User Account Activity Metrics', vendorId }]
        : ['Vendor User Account Activity Metrics']),
    }),
    getAllOnboardings: build.query<api.IPagedResults<api.OnboardingResponse>, api.OnboardingFilterParams>({
      async queryFn(params) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getAllOnboardings<api.OnboardingResponse>(params);
        return { data };
      },
      providesTags: ['Onboardings'],
    }),
    getOnboarding: build.query<api.OnboardingResponse | undefined, { id: types.UUID }>({
      async queryFn({ id: onboardingId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getOnboarding<api.OnboardingResponse>(onboardingId);
        return { data };
      },
      providesTags: (result, errors, parameters) => [{ type: 'Onboarding', id: parameters.id }],
    }),
    createOnboarding: build.mutation<api.OnboardingResponse, api.OnboardingRequest>({
      async queryFn(body) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.createOnboarding<api.OnboardingResponse>(body);
        return { data };
      },
      invalidatesTags: ['Onboardings'],
    }),
    expireOnboarding: build.mutation<api.OnboardingResponse | undefined, { id: types.UUID }>({
      async queryFn({ id }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.expireOnboarding<api.OnboardingResponse>(id);
        return { data };
      },
      invalidatesTags: (result, errors, parameters) => ['Onboardings', { type: 'Onboarding', id: parameters.id }],
    }),
    getTaskSchedules: build.query<Record<string, string>, any>({
      query() {
        return {
          url: '/operations/tasks/schedules',
          method: 'GET',
        };
      },
      providesTags: ['TaskSchedules'],
    }),
    getAllTaskExecutions: build.query<api.IPagedResults<api.VendorOperationsTaskExecutionResponse>, api.VendorOperationsTaskExecutionFilterParams>({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getAllOperationsTaskExecutions(params);
        return { data };
      },
      providesTags: ['TaskExecutions'],
    }),
    createTaskExecution: build.mutation<api.VendorOperationsTaskExecutionResponse, api.VendorOperationsTaskExecutionRequest>({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.createOperationsTaskExecution(params);
        return { data };
      },
      invalidatesTags: ['TaskExecutions'],
    }),
    getVendorActivationStatistics: build.query<api.VendorActivationsStatisticsResponse, api.VendorActivationStatisticsFilterParams>({
      async queryFn(params) {
        return await StatisticsMutex.runExclusive(async () => {
          const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
          const data = await externalVendorApi.getVendorActivationStatistics<api.VendorActivationsStatisticsResponse>(params);
          return { data };
        });
      },
    }),
    createVendorServiceAgreement: build.mutation<api.RenderedVendorServiceAgreementResponse | undefined, api.VendorServiceAgreementRequest & { vendorId: types.UUID, type: api.ServiceAgreementType }>({
      async queryFn({ vendorId, type, ...request }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.createVendorServiceAgreement(vendorId, type, request);
        return { data };
      },
      invalidatesTags: (result, _error, parameters) => (result
        ? [{ type: 'Vendor Service Agreements', vendorId: parameters.vendorId }]
        : ['Vendor Service Agreements']),
    }),
    getVendorServiceAgreements: build.query<api.IPagedResults<api.RenderedVendorServiceAgreementResponse> | undefined, api.VendorServiceAgreementFilterParams & { vendorId: string; }>({
      async queryFn({ vendorId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorServiceAgreements(vendorId, params);
        return { data };
      },
      providesTags: (result, error, parameters) => (result
        ? [{ type: 'Vendor Service Agreements', vendorId: parameters.vendorId }]
        : ['Vendor Service Agreements']),
    }),
    getVendorServiceAgreement: build.query<api.RenderedVendorServiceAgreementResponse | undefined, { vendorId: string; serviceAgreementId: string }>({
      async queryFn({ vendorId, serviceAgreementId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorServiceAgreement(vendorId, serviceAgreementId);

        return { data };
      },
      providesTags: (result, error, parameters) => (result
        ? [{ type: 'Vendor Service Agreements', serviceAgreementId: parameters.serviceAgreementId }]
        : ['Vendor Service Agreements']),
    }),
    deleteVendorServiceAgreement: build.mutation<api.RenderedVendorServiceAgreementResponse | undefined, { vendorId: string; serviceAgreementId: string }>({
      async queryFn({ vendorId, serviceAgreementId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.deleteVendorServiceAgreement(vendorId, serviceAgreementId);

        return { data };
      },
      invalidatesTags: (result, error, parameters) => (result
        ? [{ type: 'Vendor Service Agreements', serviceAgreementId: parameters.serviceAgreementId }]
        : ['Vendor Service Agreements']),
    }),
    createVendorPartnerServiceAgreement: build.mutation<api.RenderedVendorPartnerServiceAgreementResponse | undefined, api.VendorPartnerServiceAgreementRequest & { partnerId: api.Partners, vendorId: types.UUID }>({
      async queryFn({ partnerId, vendorId, ...request }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.createVendorPartnerServiceAgreement(partnerId, vendorId, request);
        return { data };
      },
      invalidatesTags: (result, _error, parameters) => (result
        ? [{ type: 'Vendor Partner Service Agreements', partnerId: parameters.partnerId, vendorId: parameters.vendorId }]
        : ['Vendor Partner Service Agreements']),
    }),
    getVendorPartnerServiceAgreements: build.query<api.IPagedResults<api.RenderedVendorPartnerServiceAgreementResponse> | undefined, api.VendorServiceAgreementFilterParams & { partnerId: api.Partners, vendorId: string; }>({
      async queryFn({ partnerId, vendorId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorPartnerServiceAgreements(partnerId, vendorId, params);
        return { data };
      },
      providesTags: (result, error, parameters) => (result
        ? [{ type: 'Vendor Partner Service Agreements', partnerId: parameters.partnerId, vendorId: parameters.vendorId }]
        : ['Vendor Partner Service Agreements']),
    }),
    getVendorPartnerServiceAgreement: build.query<api.RenderedVendorPartnerServiceAgreementResponse | undefined, { partnerId: api.Partners, vendorId: string; serviceAgreementId: string }>({
      async queryFn({ partnerId, vendorId, serviceAgreementId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorPartnerServiceAgreement(partnerId, vendorId, serviceAgreementId);

        return { data };
      },
      providesTags: (result, error, parameters) => (result
        ? [{ type: 'Vendor Partner Service Agreements', serviceAgreementId: parameters.serviceAgreementId }]
        : ['Vendor Partner Service Agreements']),
    }),
    deleteVendorPartnerServiceAgreement: build.mutation<api.RenderedVendorPartnerServiceAgreementResponse | undefined, { partnerId: api.Partners, vendorId: string; serviceAgreementId: string }>({
      async queryFn({ partnerId, vendorId, serviceAgreementId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.deleteVendorPartnerServiceAgreement(partnerId, vendorId, serviceAgreementId);

        return { data };
      },
      invalidatesTags: (result, _error, parameters) => (result
        ? [{ type: 'Vendor Partner Service Agreements', partnerId: parameters.partnerId, vendorId: parameters.vendorId }]
        : ['Vendor Partner Service Agreements']),
    }),
    getVendorDocuments: build.query<api.IPagedResults<api.VendorDocumentResponse>, api.VendorDocumentFilterParams & { vendorId: string }>({
      async queryFn({ vendorId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorDocuments<api.VendorDocumentResponse>(vendorId, params);
        return { data };
      },
      providesTags: ['Vendor Documents'],
    }),
    getAllVendorDocuments: build.query<api.IPagedResults<api.VendorDocumentResponse>, api.VendorDocumentFilterParams>({
      async queryFn(params) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getAllVendorDocuments<api.VendorDocumentResponse>(params);
        return { data };
      }
    }),
    getVendorDocument: build.query<api.VendorDocumentResponse | undefined, { vendorId: string, documentId: string }>({
      async queryFn({ vendorId, documentId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorDocument<api.VendorDocumentResponse>(vendorId, documentId);
        return { data };
      },
      providesTags: ['Vendor Documents'],
    }),
    approveVendorDocument: build.mutation<api.MaybeRedactedVendorDocumentResponse | undefined, { vendorId: string; documentId: string; verificationNotes: string } & api.ApproveVendorDocumentRequest>({
      async queryFn({ vendorId, documentId, ...request }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.approveVendorDocument(vendorId, documentId, request);
        return { data };
      },
      invalidatesTags: ['Vendor Documents', 'Vendors', 'Vendor'],
    }),
    unapproveVendorDocument: build.mutation<api.MaybeRedactedVendorDocumentResponse | undefined, { vendorId: string; documentId: string }>({
      async queryFn({ vendorId, documentId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.unapproveVendorDocument(vendorId, documentId, {});
        return { data };
      },
      invalidatesTags: ['Vendor Documents', 'Vendors', 'Vendor'],
    }),
    rejectVendorDocument: build.mutation<api.MaybeRedactedVendorDocumentResponse | undefined, { vendorId: string; documentId: string; } & api.RejectVendorDocumentRequest>({
      async queryFn({ vendorId, documentId, ...request }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.rejectVendorDocument(vendorId, documentId, request);
        return { data };
      },
      invalidatesTags: ['Vendor Documents', 'Vendors', 'Vendor'],
    }),
    hideVendorDocument: build.mutation<api.MaybeRedactedVendorDocumentResponse | undefined, { vendorId: string; documentId: string }>({
      async queryFn({ vendorId, documentId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.hideVendorDocument(vendorId, documentId, {});
        return { data };
      },
      invalidatesTags: ['Vendor Documents', 'Vendors', 'Vendor'],
    }),
    updateVendorDocumentContents: build.mutation<api.MaybeRedactedVendorDocumentResponse | undefined, { vendorId: string; documentId: string } & api.UpdateVendorDocumentContentsRequest>({
      async queryFn({ vendorId, documentId, ...request }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.updateVendorDocumentContents(vendorId, documentId, request);
        return { data };
      },
      invalidatesTags: ['Vendor Documents'],
    }),
    // scope omitted = Vendor-level Notes only; `all` also returns Notes attached to Action Items.
    getVendorNotes: build.query<readonly api.VendorNoteResponse[], { vendorId: string; scope?: api.VendorNoteScope }>({
      async queryFn({ vendorId, scope }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getAllVendorNotes(vendorId, scope ? { scope } : undefined);
        return { data };
      },
      providesTags: (result, error, { vendorId }) => [{ type: 'Vendor Notes' as const, id: vendorId }],
    }),
    addVendorNote: build.mutation<api.VendorNoteResponse, api.CreateVendorNoteRequest & { vendorId: string }>({
      async queryFn({ vendorId, ...request }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.createVendorNote(vendorId, request);
        return { data };
      },
      invalidatesTags: (result, error, { vendorId }) => [{ type: 'Vendor Notes' as const, id: vendorId }, 'Vendors In Enablement V2'],
    }),
    getVendorActionItems: build.query<readonly api.VendorActionItemResponse[], { vendorId: string }>({
      async queryFn({ vendorId }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getVendorActionItemsByVendorId(vendorId);
        return { data };
      },
      providesTags: (result, error, { vendorId }) => [{ type: 'Vendor Action Items' as const, id: vendorId }],
    }),
    // Every Vendor's items (the Action Items board); the only list that includes `vendorName`.
    getAllVendorActionItems: build.query<readonly api.VendorActionItemResponse[], api.VendorActionItemFilterParams>({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getAllVendorActionItems(params);
        return { data };
      },
      providesTags: ['All Vendor Action Items'],
    }),
    // Active Operators an item can be assigned to; needs no permissions:read, unlike getPermissionUsers.
    getVendorActionItemAssignees: build.query<readonly api.VendorActionItemAssigneeResponse[], void>({
      async queryFn() {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getVendorActionItemAssignees();
        return { data };
      },
      providesTags: ['Vendor Action Item Assignees'],
    }),
    getVendorActionItemTimeline: build.query<readonly api.VendorActionItemTimelineEntry[], { vendorId: string; vendorActionItemId: string }>({
      async queryFn({ vendorId, vendorActionItemId }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getVendorActionItemTimeline(vendorId, vendorActionItemId);
        return { data };
      },
      providesTags: (result, error, { vendorActionItemId }) => [{ type: 'Vendor Action Item Timeline' as const, id: vendorActionItemId }],
    }),
    createVendorActionItem: build.mutation<api.VendorActionItemResponse, api.CreateVendorActionItemRequest & { vendorId: string }>({
      async queryFn({ vendorId, ...request }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.createVendorActionItem(vendorId, request);
        return { data };
      },
      invalidatesTags: (result, error, { vendorId }) => [
        { type: 'Vendor Action Items' as const, id: vendorId },
        'All Vendor Action Items',
        'Vendors In Enablement V2',
      ],
    }),
    updateVendorActionItem: build.mutation<api.VendorActionItemResponse, api.UpdateVendorActionItemRequest & { vendorId: string; vendorActionItemId: string }>({
      async queryFn({ vendorId, vendorActionItemId, ...request }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.updateVendorActionItem(vendorId, vendorActionItemId, request);
        return { data };
      },
      // Status changes (board drags, Move to…) patch every cached list immediately so the card lands in its new
      // column before the refetch; the patch is undone if the write fails.
      async onQueryStarted({ vendorId, vendorActionItemId, status }, { dispatch, getState, queryFulfilled }) {
        if (!status) return;
        const withStatus = (items: readonly api.VendorActionItemResponse[]) => (
          items.map((item) => (item.id === vendorActionItemId ? { ...item, status } : item))
        );
        const cached = vendorsApi.util.selectInvalidatedBy(getState(), [
          'All Vendor Action Items',
          { type: 'Vendor Action Items' as const, id: vendorId },
        ]);
        const patches = cached.map(({ endpointName, originalArgs }) => (endpointName === 'getAllVendorActionItems'
          ? dispatch(vendorsApi.util.updateQueryData('getAllVendorActionItems', originalArgs, withStatus))
          : dispatch(vendorsApi.util.updateQueryData('getVendorActionItems', originalArgs, withStatus))));

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: (result, error, { vendorId, vendorActionItemId }) => [
        { type: 'Vendor Action Items' as const, id: vendorId },
        { type: 'Vendor Action Item Timeline' as const, id: vendorActionItemId },
        'All Vendor Action Items',
        'Vendors In Enablement V2',
      ],
    }),
    createVendorActionItemNote: build.mutation<api.VendorNoteResponse, api.CreateVendorNoteRequest & { vendorId: string; vendorActionItemId: string }>({
      async queryFn({ vendorId, vendorActionItemId, ...request }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.createVendorActionItemNote(vendorId, vendorActionItemId, request);
        return { data };
      },
      // Item Notes change the item's latest Note, note count and timeline, and show in the `all`-scope Notes feed.
      invalidatesTags: (result, error, { vendorId, vendorActionItemId }) => [
        { type: 'Vendor Action Items' as const, id: vendorId },
        { type: 'Vendor Action Item Timeline' as const, id: vendorActionItemId },
        { type: 'Vendor Notes' as const, id: vendorId },
        'All Vendor Action Items',
      ],
    }),
    getVendorUccFilings: build.query<api.IPagedResults<api.VendorUccFilingResponse>, { vendorId: string } & api.IPageFilterParams>({
      async queryFn({ vendorId, ...params }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getAllVendorUccFilings(vendorId, params);
        return { data };
      },
      providesTags: ['Vendor UCC Filings'],
    }),
    createVendorUccFiling: build.mutation<api.VendorUccFilingResponse, api.CreateVendorUccFilingRequest & { vendorId: string }>({
      async queryFn({ vendorId, ...request }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.createVendorUccFiling(vendorId, request);
        return { data };
      },
      invalidatesTags: ['Vendor UCC Filings'],
    }),
    updateVendorUccFiling: build.mutation<api.VendorUccFilingResponse, api.UpdateVendorUccFilingRequest & { vendorId: string; uccFilingId: string }>({
      async queryFn({ vendorId, uccFilingId, ...request }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.updateVendorUccFiling(vendorId, uccFilingId, request);
        return { data };
      },
      invalidatesTags: ['Vendor UCC Filings'],
    }),
    getAllUccFilings: build.query<api.IPagedResults<api.VendorUccFilingResponse>, api.UccFilingFilterParams>({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getAllUccFilings(params);
        return { data };
      },
      providesTags: ['Vendor UCC Filings'],
    }),
    getAllVendorSubAccounts: build.query<api.IPagedResults<api.VendorSubAccountResponse>, api.VendorSubAccountFilterParams>({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getAllVendorSubAccounts(params);
        return { data };
      },
      providesTags: ['Vendor Sub Accounts'],
    }),
    getNextVendorSubAccountValue: build.query<{ nextValue: string }, void>({
      async queryFn() {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getNextVendorSubAccountValue();
        return { data };
      },
    }),
    createVendorSubAccount: build.mutation<api.VendorSubAccountResponse, api.VendorSubAccountRequest>({
      async queryFn(request) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.createVendorSubAccount(request);
        return { data };
      },
      invalidatesTags: ['Vendor Sub Accounts'],
    }),
    updateVendorSubAccount: build.mutation<api.VendorSubAccountResponse, api.UpdateVendorSubAccountRequest & { subAccountId: string }>({
      async queryFn({ subAccountId, ...request }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.updateVendorSubAccount(subAccountId, request);
        return { data };
      },
      invalidatesTags: ['Vendor Sub Accounts'],
    }),
    deleteVendorSubAccount: build.mutation<void, { subAccountId: string }>({
      async queryFn({ subAccountId }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.deleteVendorSubAccount(subAccountId);
        return { data };
      },
      invalidatesTags: ['Vendor Sub Accounts'],
    }),
    getVendorAudit: build.query<api.IPagedResults<api.VendorsAuditResponse>, api.VendorAuditFilterParams & { vendorId: string }>({
      async queryFn({ vendorId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorAudit(vendorId, params);
        return { data };
      },
      providesTags: ['Vendor Audit'],
    }),
    getThirdPartyBusinessVerifications: build.query<any[], { vendorId: string }>({
      query({ vendorId }) {
        return {
          url: `/vendors/${vendorId}/third-party-business-verifications`,
          method: 'GET',
        };
      },
      providesTags: ['Third Party Business Verifications'],
    }),
    uploadFacilitators: build.mutation<readonly api.FacilitatorResponse[], { body: FormData, partnerId: number }>({
      async queryFn({ body, partnerId }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.uploadFacilitators(partnerId, body);
        return { data };
      },
      invalidatesTags: ['Facilitators'],
    }),
    getAllFacilitators: build.query<api.IPagedResults<api.FacilitatorResponse>, api.FacilitatorFilterParams>({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getAllFacilitators(params);
        return { data };
      },
      providesTags: ['Facilitators'],
    }),
    getFacilitatorTotals: build.query<api.FacilitatorTotalsResponse, unknown>({
      async queryFn() {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getFacilitatorTotals();
        return { data };
      },
      providesTags: ['FacilitatorTotals'],
    }),
    approveFacilitators: build.mutation<readonly api.FacilitatorResponse[], { ids: readonly api.UUID[] }>({
      async queryFn({ ids }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await ids.reduce((p, id) => p.then(async (res) => {
          const facilitator = await vendorApi.approveFacilitator(id, {});
          return facilitator ? [...res, facilitator] : res;
        }), Promise.resolve([] as readonly api.FacilitatorResponse[]));

        return { data };
      },
      invalidatesTags: ['Facilitators'],
    }),
    revokeFacilitators: build.mutation<readonly api.FacilitatorResponse[], { ids: readonly api.UUID[] }>({
      async queryFn({ ids }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await ids.reduce((p, id) => p.then(async (res) => {
          const facilitator = await vendorApi.revokeFacilitator(id, {});
          return facilitator ? [...res, facilitator] : res;
        }), Promise.resolve([] as readonly api.FacilitatorResponse[]));

        return { data };
      },
      invalidatesTags: ['Facilitators'],
    }),
    uploadVendorDocument: build.mutation<api.MaybeRedactedVendorDocumentResponse | undefined, { vendorId: string, body: FormData }>({
      async queryFn({ vendorId, body }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.uploadVendorDocument(vendorId, body);
        return { data };
      },
      invalidatesTags: ['Vendor', 'Vendor Documents'],
    }),
    bulkApproveVendorDocuments: build.mutation<api.BulkApproveVendorDocumentsResponse, api.BulkApproveVendorDocumentsRequest & { vendorId: string }>({
      async queryFn({ vendorId, ...body }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.bulkApproveVendorDocuments(vendorId, body);
        return { data };
      },
      invalidatesTags: ['Vendor', 'Vendor Documents'],
    }),
    importVendorAccountTransactions: build.mutation<api.ImportVendorAccountUnderwritingTransactionsResponse, { vendorId: string; vendorAccountId: string; body: FormData }>({
      async queryFn({ vendorId, vendorAccountId, body }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.importVendorAccountUnderwritingTransactions(vendorId, vendorAccountId, body);
        return { data };
      },
      invalidatesTags: ['Vendor Account Underwriting Transactions', 'Vendor Account Underwriting Balance'],
    }),
    patchUserAccount: build.mutation<api.MaybeRedactedUserAccountResponse | undefined, api.PatchUserAccountRequest & { id: string }>({
      async queryFn({ id, ...body }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.patchUserAccount(id, body);
        return { data };
      },
      invalidatesTags: ['User Accounts'],
    }),
    getPrograms:
      build.query<readonly api.ProgramResponse[], api.GetProgramsFilterParams>({
        async queryFn(params) {
          const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
          const data = await externalVendorApi.getPrograms<api.ProgramResponse>(params);
          return { data };
        },
        providesTags: ['Programs'],
      }),
    getProgramStatistics:
      build.query<api.ProgramStatisticsBreakdown, api.GetProgramsFilterParams>({
        async queryFn(params) {
          const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
          const data = await externalVendorApi.getProgramsStatistics<api.ProgramStatisticsBreakdown>(params);
          return { data };
        },
      }),
    getProgramOverrides:
      build.query<api.IPagedResults<api.VendorProgramOverrideResponse>, api.GetProgramOverridesFilterParams>({
        async queryFn(params) {
          const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
          const data = await externalVendorApi.getProgramOverrides<api.VendorProgramOverrideResponse>(params);
          return { data };
        },
      }),
    getProgram:
      build.query<api.ProgramResponse, { code: string, partnerId: number }>({
        async queryFn({ code, partnerId }) {
          const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
          const data = await externalVendorApi.getProgram<api.ProgramResponse>(code as ProgramCodes, partnerId);
          return { data };
        },
        providesTags: ['Program']
      }),
    patchProgram: build.mutation<api.ProgramResponse, api.PatchProgramRequest & { code: string, partnerId: number }>({
      async queryFn({ code, partnerId, ...body }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.patchProgram(code as ProgramCodes, partnerId, body);
        return { data };
      },
      invalidatesTags: ['Program'],
    }),
    patchVendorFeatures: build.mutation<api.VendorAggregateResponse | undefined, api.PatchVendorFeaturesRequest & { vendorId: api.UUID }>({
      async queryFn({ vendorId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.patchVendorFeatures(vendorId, params);
        return { data };
      },
      invalidatesTags: ['Vendor'],
    }),
    getAllVendorSurveys: build.query<api.IPagedResults<api.VendorSurveyResponse>, api.VendorSurveyFilterParams>({
      async queryFn(params) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getAllVendorSurveys<api.VendorSurveyResponse>(params);
        return { data };
      },
      providesTags: ['Vendor Surveys']
    }),
    getAllVendorThirdPartyBusinessVerifications: build.query<api.VendorThirdPartyBusinessVerificationResponse[], { vendorId: api.UUID }>({
      async queryFn(params) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getAllVendorThirdPartyBusinessVerifications(params.vendorId);

        return { data };
      },
    }),
    markPartnerEnabled: build.mutation<api.VendorResponse | undefined, { vendorId: api.UUID; }>({
      async queryFn({ vendorId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.markPartnerEnabled(vendorId, {});
        return { data };
      },
      invalidatesTags: (result: any) => ['Vendors', { type: 'Vendor', id: result.id }, 'Vendor Audit'],
    }),
    // Underwriting
    getVendorUnderwriting: build.query<api.VendorUnderwritingResponse | undefined, { vendorId: api.UUID; vendorAccountId?: api.UUID }>({
      async queryFn({ vendorId, vendorAccountId }) {
        if (isDev) {
          return { data: hardcodedVendorUnderwriting(vendorId, vendorAccountId) };
        }
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorUnderwriting(vendorId);
        return { data };
      },
      providesTags: ['Vendor Underwriting'],
    }),
    updateVendorUnderwriting: build.mutation<api.VendorUnderwritingResponse | undefined, { vendorId: api.UUID } & api.UpdateVendorUnderwritingRequest>({
      async queryFn({ vendorId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.patchVendorUnderwriting(vendorId, params);
        return { data };
      },
      invalidatesTags: (_result, _error, { vendorId }) => ['Vendor Underwriting', 'Vendor Limit Audit Log', { type: 'Vendor Notes' as const, id: vendorId }],
    }),
    getVendorLimitAuditLog: build.query<api.IPagedResults<api.VendorAuditLogEntry>, api.AuditLogQuery | void>({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getVendorLimitAuditLog(params ?? undefined);
        return { data };
      },
      providesTags: ['Vendor Limit Audit Log'],
    }),
    // Vendor Underwriting Assessments
    getAllVendorUnderwritingAssessments: build.query<api.IPagedResults<api.VendorUnderwritingAssessmentResponse>, { vendorId: api.UUID } & api.VendorUnderwritingAssessmentFilterParams>({
      async queryFn({ vendorId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getAllVendorUnderwritingAssessments(vendorId, params);
        return { data };
      },
      providesTags: ['Vendor Underwriting Assessments'],
    }),
    getVendorUnderwritingAssessment: build.query<api.VendorUnderwritingAssessmentResponse | undefined, { vendorId: api.UUID; assessmentId: api.UUID }>({
      async queryFn({ vendorId, assessmentId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorUnderwritingAssessment(vendorId, assessmentId);
        return { data };
      },
      providesTags: ['Vendor Underwriting Assessments'],
    }),
    createVendorUnderwritingAssessment: build.mutation<api.VendorUnderwritingAssessmentResponse, { vendorId: api.UUID } & api.VendorUnderwritingAssessmentRequest>({
      async queryFn({ vendorId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.createVendorUnderwritingAssessment(vendorId, params);
        return { data };
      },
      invalidatesTags: ['Vendor Underwriting Assessments'],
    }),
    approveVendorUnderwritingAssessment: build.mutation<api.VendorUnderwritingAssessmentResponse | undefined, { vendorId: api.UUID; assessmentId: api.UUID } & api.ApproveVendorUnderwritingAssessmentRequest>({
      async queryFn({ vendorId, assessmentId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.approveVendorUnderwritingAssessment(vendorId, assessmentId, params);
        return { data };
      },
      invalidatesTags: ['Vendor Underwriting Assessments'],
    }),
    rejectVendorUnderwritingAssessment: build.mutation<api.VendorUnderwritingAssessmentResponse | undefined, { vendorId: api.UUID; assessmentId: api.UUID } & api.RejectVendorUnderwritingAssessmentRequest>({
      async queryFn({ vendorId, assessmentId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.rejectVendorUnderwritingAssessment(vendorId, assessmentId, params);
        return { data };
      },
      invalidatesTags: ['Vendor Underwriting Assessments'],
    }),
    expireVendorUnderwritingAssessment: build.mutation<api.VendorUnderwritingAssessmentResponse | undefined, { vendorId: api.UUID; assessmentId: api.UUID } & api.ExpireVendorUnderwritingAssessmentRequest>({
      async queryFn({ vendorId, assessmentId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.expireVendorUnderwritingAssessment(vendorId, assessmentId, params);
        return { data };
      },
      invalidatesTags: ['Vendor Underwriting Assessments'],
    }),
    cloneVendorUnderwritingAssessment: build.mutation<api.VendorUnderwritingAssessmentResponse, { vendorId: api.UUID; assessmentId: api.UUID } & api.CloneVendorUnderwritingAssessmentRequest>({
      async queryFn({ vendorId, assessmentId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.cloneVendorUnderwritingAssessment(vendorId, assessmentId, params);
        return { data };
      },
      invalidatesTags: ['Vendor Underwriting Assessments'],
    }),
    updateVendorUnderwritingAssessment: build.mutation<api.VendorUnderwritingAssessmentResponse | undefined, { vendorId: api.UUID; assessmentId: api.UUID } & api.UpdateVendorUnderwritingAssessmentRequest>({
      async queryFn({ vendorId, assessmentId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.updateVendorUnderwritingAssessment(vendorId, assessmentId, params);
        return { data };
      },
      invalidatesTags: ['Vendor Underwriting Assessments'],
    }),
    getVendorAccountUnderwritingBalance: build.query<api.VendorAccountUnderwritingBalanceResponse | undefined, { vendorId: api.UUID; vendorAccountId: api.UUID }>({
      async queryFn({ vendorId, vendorAccountId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorAccountUnderwritingCurrentBalance(vendorId, vendorAccountId);
        return { data };
      },
      providesTags: ['Vendor Account Underwriting Balance']
    }),
    getVendorAccountUnderwritingTransactions: build.query<api.IPagedResults<api.VendorAccountUnderwritingTransactionResponse>, { vendorId: api.UUID; vendorAccountId: api.UUID } & api.VendorAccountUnderwritingTransactionFilterParams>({
      async queryFn({ vendorId, vendorAccountId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorAccountUnderwritingTransactions(vendorId, vendorAccountId, params);
        return { data };
      },
      providesTags: ['Vendor Account Underwriting Transactions']
    }),
    getVendorAccountUnderwritingTransaction: build.query<api.VendorAccountUnderwritingTransactionResponse, { transactionId: api.UUID }>({
      async queryFn({ transactionId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorAccountUnderwritingTransaction({ transactionId });
        return { data };
      },
      providesTags: (result, error, { transactionId }) =>
        result ? [{ type: 'Vendor Account Underwriting Transactions', id: transactionId }] : ['Vendor Account Underwriting Transactions'],
    }),
    getUnderwritingTransactionLabellingDetails: build.query<api.IPagedResults<api.VendorAccountUnderwritingTransactionLabellingDetailsResponse>, api.VendorAccountUnderwritingTransactionLabellingDetailsFilterParams | undefined>({
      async queryFn(params) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getUnderwritingTransactionLabellingDetails(params ?? {});
        return { data };
      },
      providesTags: ['Underwriting Transaction Labelling Details'],
    }),
    getUnderwritingTransactionLabellingDetail: build.query<api.VendorAccountUnderwritingTransactionLabellingDetailsResponse | undefined, { transactionId: api.UUID }>({
      async queryFn({ transactionId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const result = await externalVendorApi.getUnderwritingTransactionLabellingDetails({ transactionId });
        const data = result?.data?.[0];
        return { data };
      },
      providesTags: (result, error, { transactionId }) =>
        result ? [{ type: 'Underwriting Transaction Labelling Details', id: transactionId }] : ['Underwriting Transaction Labelling Details'],
    }),
    patchUnderwritingTransactionLabelling: build.mutation<api.VendorAccountUnderwritingTransactionLabellingDetailsResponse, { transactionId: api.UUID; overriddenCategory?: string | null; overriddenSubcategory?: string | null }>({
      async queryFn({ transactionId, overriddenCategory, overriddenSubcategory }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const request = { overriddenCategory, overriddenSubcategory };
        const apiWithPatch = externalVendorApi as unknown as { patchUnderwritingTransactionLabelling: (id: string, body: { overriddenCategory?: string | null; overriddenSubcategory?: string | null }) => Promise<api.VendorAccountUnderwritingTransactionLabellingDetailsResponse> };
        const data = await apiWithPatch.patchUnderwritingTransactionLabelling(transactionId, request);
        return { data };
      },
      invalidatesTags: (result, error, { transactionId }) =>
        result ? [{ type: 'Underwriting Transaction Labelling Details', id: transactionId }, 'Underwriting Transaction Labelling Details', { type: 'Vendor Account Underwriting Transactions', id: transactionId }] : ['Underwriting Transaction Labelling Details'],
    }),
    getVendorAccountUnderwritingSummaryTimeSeries: build.query<api.VendorAccountUnderwritingSummaryTimeSeriesResponse, { vendorId: api.UUID; vendorAccountId?: api.UUID } & api.VendorAccountUnderwritingSummaryTimeSeriesParams>({
      async queryFn({ vendorId, vendorAccountId, ...params }) {
        if (isDev) {
          return { data: hardcodedTimeSeries as api.VendorAccountUnderwritingSummaryTimeSeriesResponse };
        }
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorAccountUnderwritingSummaryTimeSeries(vendorId, vendorAccountId, params);
        return { data };
      },
    }),
    getVendorAccountUnderwritingCounterpartySummaries: build.query<api.VendorAccountUnderwritingCounterpartySummariesResponse, { vendorId: api.UUID; vendorAccountId: api.UUID } & api.VendorAccountUnderwritingCounterpartySummariesParams>({
      async queryFn({ vendorId, vendorAccountId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorAccountUnderwritingCounterpartySummaries(vendorId, vendorAccountId, params);
        return { data };
      },
    }),
    createVendorAccountUnderwritingTransactionMatchedInvoices: build.mutation<readonly api.VendorAccountUnderwritingTransactionMatchedInvoiceResponse[], { vendorId: api.UUID; vendorAccountId: api.UUID } & api.CreateVendorAccountUnderwritingTransactionMatchedInvoicesRequest>({
      async queryFn({ vendorId, vendorAccountId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.createVendorAccountUnderwritingTransactionMatchedInvoices(vendorId, vendorAccountId, params);
        return { data };
      },
      invalidatesTags: (result, error, parameters) => (result
        ? [{ type: 'Vendor Account Underwriting Transaction Matched Invoices', vendorId: parameters.vendorId, vendorAccountId: parameters.vendorAccountId }]
        : ['Vendor Account Underwriting Transaction Matched Invoices']),
    }),
    getVendorAccountUnderwritingTransactionMatchedInvoices: build.query<api.IPagedResults<api.VendorAccountUnderwritingTransactionMatchedInvoiceResponse>, { vendorId: api.UUID; vendorAccountId: api.UUID; } & api.VendorAccountUnderwritingTransactionMatchedInvoicesFilterParams>({
      async queryFn({ vendorId, vendorAccountId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.getVendorAccountUnderwritingTransactionMatchedInvoices(vendorId, vendorAccountId, params);
        return { data };
      },
      providesTags: (result, error, parameters) => (result
        ? [{ type: 'Vendor Account Underwriting Transaction Matched Invoices', vendorId: parameters.vendorId, vendorAccountId: parameters.vendorAccountId }]
        : ['Vendor Account Underwriting Transaction Matched Invoices']),
    }),
    deleteVendorAccountUnderwritingTransactionMatchedInvoices: build.mutation<void, { vendorId: api.UUID; vendorAccountId: api.UUID; matchedInvoiceIds: readonly api.UUID[] }>({
      async queryFn({ vendorId, vendorAccountId, matchedInvoiceIds }, _queryApi, _extraOptions, fetchWithBQ) {
        const responses = await Promise.all(matchedInvoiceIds.map((matchedInvoiceId) => fetchWithBQ({
          url: `/vendors/${vendorId}/accounts/${vendorAccountId}/underwriting/transactions/matched-invoices/${matchedInvoiceId}`,
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        })));

        return { data: undefined };
      },
      invalidatesTags: (result, error, parameters) => (result
        ? [{ type: 'Vendor Account Underwriting Transaction Matched Invoices', vendorId: parameters.vendorId, vendorAccountId: parameters.vendorAccountId }, 'Vendor Matching Suggestions']
        : ['Vendor Account Underwriting Transaction Matched Invoices', 'Vendor Matching Suggestions']),
    }),
    suggestVendorAccountUnderwritingTransactionMatchedInvoices: build.mutation<readonly api.SuggestedVendorAccountUnderwritingTransactionMatchedInvoiceResponse[], { vendorId: api.UUID; vendorAccountId: api.UUID }>({
      async queryFn({ vendorId, vendorAccountId }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.suggestVendorAccountUnderwritingTransactionMatchedInvoices(vendorId, vendorAccountId);
        return { data };
      },
    }),
    suggestVendorMatchedInvoices: build.query<api.MatchingSuggestionsResponse, { vendorId: api.UUID; params?: api.ReconcileMatchingParams }>({
      async queryFn({ vendorId, params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.suggestVendorMatchedInvoices(vendorId, params);
        return { data };
      },
      providesTags: ['Vendor Matching Suggestions'],
    }),
    updateVendorAccountUnderwriting: build.mutation<void, types.UpdateVendorAccountUnderwritingRequest & { vendorId: api.UUID; vendorAccountId: api.UUID }>({
      async queryFn({ vendorId, vendorAccountId, ...params }) {
        const externalVendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await externalVendorApi.updateVendorAccountUnderwriting(vendorId, vendorAccountId, params);
        return { data };
      },
      invalidatesTags: ['Vendor Underwriting']
    }),
    putVendorAccountTargets: build.mutation<types.RedactedVendorAccountResponse, types.PutVendorAccountTargetsRequest & { vendorId: api.UUID; vendorAccountId: api.UUID }>({
      async queryFn({ vendorId, vendorAccountId, ...params }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.putVendorAccountTargets<types.RedactedVendorAccountResponse>(vendorId, vendorAccountId, params);
        return { data };
      },
      invalidatesTags: (_result, _error, parameters) => ['Vendors', { type: 'Vendor', id: parameters.vendorId }],
    }),
    globalSearch: build.query<types.VendorGlobalSearchResponse, types.GlobalSearchFilterParams>({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.globalSearch(params);
        return { data };
      },
    }),
    getAllVendorEvents: build.query<api.IPagedResults<api.VendorEventResponse>, { vendorId: api.UUID } & api.VendorEventsFilterParams>({
      async queryFn({ vendorId, ...params }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getAllVendorEvents(vendorId, params);
        return { data };
      },
    }),
    getAllVendorEmailSequences: build.query<api.IPagedResults<api.VendorEmailSequenceResponse>, { vendorId: api.UUID } & api.IPageFilterParams>({
      async queryFn({ vendorId, ...params }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getAllVendorEmailSequences(vendorId, params);
        return { data };
      },
      providesTags: ['Vendor Email Sequences'],
    }),
    // MESA-4981: every vendor's sequences, for the Vendor Email Sequences overview page.
    getAllVendorEmailSequencesAcrossVendors: build.query<api.IPagedResults<api.VendorEmailSequenceListItemResponse>, api.VendorEmailSequenceFilterParams>({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getAllVendorEmailSequencesAcrossVendors(params);
        return { data };
      },
      providesTags: ['Vendor Email Sequences'],
    }),
    bulkVendorEmailSequenceAction: build.mutation<api.VendorEmailSequenceBulkActionResponse, api.VendorEmailSequenceBulkActionRequest>({
      async queryFn(request) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.bulkVendorEmailSequenceAction(request);
        return { data };
      },
      invalidatesTags: ['Vendor Email Sequences'],
    }),
    pauseVendorEmailSequence: build.mutation<api.VendorEmailSequenceResponse, { vendorId: api.UUID; id: api.UUID }>({
      async queryFn({ vendorId, id }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.pauseVendorEmailSequence(vendorId, id);
        return { data };
      },
      invalidatesTags: ['Vendor Email Sequences'],
    }),
    resumeVendorEmailSequence: build.mutation<api.VendorEmailSequenceResponse, { vendorId: api.UUID; id: api.UUID }>({
      async queryFn({ vendorId, id }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.resumeVendorEmailSequence(vendorId, id);
        return { data };
      },
      invalidatesTags: ['Vendor Email Sequences'],
    }),
    activateVendorEmailSequence: build.mutation<api.VendorEmailSequenceResponse, { vendorId: api.UUID; id: api.UUID }>({
      async queryFn({ vendorId, id }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.activateVendorEmailSequence(vendorId, id);
        return { data };
      },
      invalidatesTags: ['Vendor Email Sequences'],
    }),
    deactivateVendorEmailSequence: build.mutation<api.VendorEmailSequenceResponse, { vendorId: api.UUID; id: api.UUID }>({
      async queryFn({ vendorId, id }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.deactivateVendorEmailSequence(vendorId, id);
        return { data };
      },
      invalidatesTags: ['Vendor Email Sequences'],
    }),
    cloneVendorEmailSequence: build.mutation<api.VendorEmailSequenceResponse, { vendorId: api.UUID; id: api.UUID }>({
      async queryFn({ vendorId, id }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.cloneVendorEmailSequence(vendorId, id);
        return { data };
      },
      invalidatesTags: ['Vendor Email Sequences'],
    }),
    createVendorEmailSequenceFromTemplate: build.mutation<api.VendorEmailSequenceResponse, { vendorId: api.UUID; emailSequenceId: api.UUID }>({
      async queryFn({ vendorId, emailSequenceId }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.createVendorEmailSequenceFromTemplate(vendorId, { emailSequenceId });
        return { data };
      },
      invalidatesTags: ['Vendor Email Sequences'],
    }),
    sendVendorEmailSequenceStepNow: build.mutation<api.VendorEmailSequenceResponse, { vendorId: api.UUID; id: api.UUID; stepId: api.UUID }>({
      async queryFn({ vendorId, id, stepId }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.sendVendorEmailSequenceStepNow(vendorId, id, stepId);
        return { data };
      },
      invalidatesTags: ['Vendor Email Sequences'],
    }),
    getVendorEmailSequence: build.query<api.VendorEmailSequenceResponse | undefined, { vendorId: api.UUID; id: api.UUID }>({
      async queryFn({ vendorId, id }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getVendorEmailSequence(vendorId, id);
        return { data };
      },
      providesTags: ['Vendor Email Sequences'],
    }),
    /** MESA-5004: live true/false of every condition -- same tag, so it refetches after save/pause/etc. */
    getVendorEmailSequenceConditionEvaluations: build.query<api.VendorEmailSequenceConditionEvaluationsResponse | undefined, { vendorId: api.UUID; id: api.UUID }>({
      async queryFn({ vendorId, id }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getVendorEmailSequenceConditionEvaluations(vendorId, id);
        return { data };
      },
      providesTags: ['Vendor Email Sequences'],
    }),
    /** MESA-4976: atomic save of the sequence's conditions/deadline and complete step list. */
    saveVendorEmailSequence: build.mutation<api.VendorEmailSequenceResponse, api.SaveVendorEmailSequenceRequest & { vendorId: api.UUID; id: api.UUID }>({
      async queryFn({ vendorId, id, ...body }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.saveVendorEmailSequence(vendorId, id, body);
        return { data };
      },
      invalidatesTags: ['Vendor Email Sequences'],
    }),
    getAllConfigurationSettings: build.query<readonly api.VendorConfigurationSettingResponse[], void>({
      async queryFn() {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getAllConfigurationSettings();
        return { data };
      },
      providesTags: ['ConfigurationSettings'],
    }),
    updateConfigurationSetting: build.mutation<api.VendorConfigurationSettingResponse | undefined, { name: string; } & api.VendorConfigurationSettingRequest>({
      async queryFn({ name, ...request }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.updateConfigurationSetting(name, request);
        return { data };
      },
      invalidatesTags: ['ConfigurationSettings'],
    }),
    requestSqlQueryAnswer: build.mutation<api.SqlQuestionResponse, api.SqlQuestionRequest>({
      async queryFn(request) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.requestSqlQueryAnswer(request);
        return { data };
      },
    }),
    getUnderwritingLabelMetricsCoverage: build.query<
      api.UnderwritingLabelMetricsCoverageResponse,
      api.UnderwritingLabelMetricsCoverageParams | undefined
    >({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getUnderwritingLabelMetricsCoverage(params);
        return { data };
      },
    }),
    getVendorAccountUnderwritingDashboard: build.query<api.UnderwritingDashboardResponse, api.UnderwritingDashboardParams>({
      async queryFn(params) {
        if (isDev) {
          return { data: hardcodedUnderwritingDashboard as api.UnderwritingDashboardResponse };
        }
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getVendorAccountUnderwritingDashboard(params);
        return { data };
      },
      providesTags: ['Vendor Account Underwriting Dashboard'],
    }),
    getVendorAccountUnderwritingCashFlowCategories: build.query<api.UnderwritingCashFlowCategoriesResponse, api.UnderwritingCashFlowCategoriesParams>({
      async queryFn(params) {
        if (isDev) {
          return { data: hardcodedCashFlowCategories as api.UnderwritingCashFlowCategoriesResponse };
        }
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getVendorAccountUnderwritingCashFlowCategories(params);
        return { data };
      },
      providesTags: ['Vendor Account Underwriting Cash Flow Categories'],
    }),
    getVendorAccountUnderwritingCashFlowSubcategories: build.query<api.UnderwritingCashFlowSubcategoriesResponse, api.UnderwritingCashFlowSubcategoriesParams>({
      async queryFn(params) {
        if (isDev) {
          return { data: hardcodedCashFlowSubcategories as api.UnderwritingCashFlowSubcategoriesResponse };
        }
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getVendorAccountUnderwritingCashFlowSubcategories(params);
        return { data };
      },
      providesTags: ['Vendor Account Underwriting Cash Flow Subcategories'],
    }),
    getVendorAccountUnderwritingTransactionMetadata: build.query<api.UnderwritingTransactionMetadataResponse, api.UnderwritingTransactionMetadataParams>({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getVendorAccountUnderwritingTransactionMetadata(params);
        return { data };
      },
      providesTags: ['Vendor Account Underwriting Cash Flow Subcategories'],
    }),
    getAllVendorProgramPromotions: build.query<api.IPagedResults<api.VendorProgramPromotionResponse>, api.VendorProgramPromotionFilterParams>({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getAllVendorProgramPromotions(params);
        return { data };
      },
      providesTags: ['Vendor Program Promotions'],
    }),
    getVendorProgramPromotion: build.query<api.VendorProgramPromotionResponse | undefined, api.UUID>({
      async queryFn(id) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getVendorProgramPromotion(id);
        return { data };
      },
      providesTags: (_, __, id) => [{ type: 'Vendor Program Promotions', id }],
    }),
    createVendorProgramPromotions: build.mutation<api.CreateVendorProgramPromotionsResponse, api.CreateVendorProgramPromotionsRequest>({
      async queryFn(request) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.createVendorProgramPromotions(request);
        return { data };
      },
      invalidatesTags: ['Vendor Program Promotions'],
    }),
    createMultiPartnerUpsellVendorProgramPromotions: build.mutation<api.CreateMultiPartnerUpsellVendorProgramPromotionsResponse, api.CreateMultiPartnerUpsellVendorProgramPromotionsRequest>({
      async queryFn(request) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.createMultiPartnerUpsellVendorProgramPromotions(request);
        return { data };
      },
      invalidatesTags: ['Vendor Program Promotions'],
    }),
    uploadVendorProgramPromotions: build.mutation<api.CreateVendorProgramPromotionsResponse, { body: FormData }>({
      async queryFn({ body }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.uploadVendorProgramPromotions(body);
        return { data };
      },
      invalidatesTags: ['Vendor Program Promotions'],
    }),
    patchVendorProgramPromotion: build.mutation<api.VendorProgramPromotionResponse | undefined, api.PatchVendorProgramPromotionRequest & { id: api.UUID }>({
      async queryFn({ id, ...request }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.patchVendorProgramPromotion(id, request);
        return { data };
      },
      invalidatesTags: ['Vendor Program Promotions'],
    }),
    overrideVendorProgramPromotionSelectedInvoice: build.mutation<api.VendorProgramPromotionResponse | undefined, { promotionId: api.UUID, invoiceId: api.UUID }>({
      async queryFn({ promotionId, invoiceId }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.putVendorProgramPromotionSelectedInvoiceOverride(promotionId, { invoiceId });
        return { data };
      },
      invalidatesTags: ['Vendor Program Promotions'],
    }),
    clearVendorProgramPromotionSelectedInvoiceOverride: build.mutation<api.VendorProgramPromotionResponse | undefined, { promotionId: api.UUID }>({
      async queryFn({ promotionId }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.deleteVendorProgramPromotionSelectedInvoiceOverride(promotionId);
        return { data };
      },
      invalidatesTags: ['Vendor Program Promotions'],
    }),

    /** Program Promotions (program-level defaults) */
    getProgramPromotions: build.query<readonly api.ProgramPromotionResponse[], { code: string, partnerId: number }>({
      async queryFn({ code, partnerId }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getProgramPromotions(code as ProgramCodes, partnerId);
        return { data };
      },
      providesTags: ['Program Promotions']
    }),
    putProgramPromotion: build.mutation<api.ProgramPromotionResponse, api.PutProgramPromotionRequest & { code: string, partnerId: number, type: api.VendorProgramPromotionType }>({
      async queryFn({ code, partnerId, type, ...body }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.putProgramPromotion(code as ProgramCodes, partnerId, type, body);
        return { data };
      },
      invalidatesTags: ['Program Promotions'],
    }),
    deleteProgramPromotion: build.mutation<api.ProgramPromotionResponse | undefined, { code: string, partnerId: number, type: api.VendorProgramPromotionType }>({
      async queryFn({ code, partnerId, type }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.deleteProgramPromotion(code as ProgramCodes, partnerId, type);
        return { data };
      },
      invalidatesTags: ['Program Promotions'],
    }),
    approveVendorProgramPromotion: build.mutation<api.VendorProgramPromotionResponse | undefined, api.UUID>({
      async queryFn(id) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.approveVendorProgramPromotion(id);
        return { data };
      },
      invalidatesTags: ['Vendor Program Promotions'],
    }),

    /** Enablement V2 */
    getPartnerStatusSummary: build.query<api.PartnerStatusSummaryResponse, void>({
      async queryFn() {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getPartnerStatusSummary();
        return { data };
      },
      providesTags: ['Vendors In Enablement V2'],
    }),
    getVendorStatusSummary: build.query<api.IPagedResults<api.VendorStatusSummaryItem>, api.VendorStatusSummaryFilterParams>({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getVendorStatusSummary(params);
        return { data };
      },
      providesTags: ['Vendors In Enablement V2'],
    }),
    getVendorUpsellStatusSummary: build.query<api.IPagedResults<api.VendorUpsellStatusSummaryItem>, api.VendorUpsellStatusSummaryFilterParams>({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getVendorUpsellStatusSummary(params);
        return { data };
      },
      providesTags: ['Vendors In Enablement V2'],
    }),
    getVendorStatusSummaryById: build.query<api.VendorStatusSummaryItem | undefined, { vendorId: api.UUID }>({
      async queryFn({ vendorId }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getVendorStatusSummaryById(vendorId);
        return { data };
      },
      providesTags: (result, error, { vendorId }) => [{ type: 'Vendor', id: vendorId }],
    }),
  }),
});

export const {
  useGetAllVendorsQuery,
  useLazyGetAllVendorsQuery,
  useGetVendorQuery,
  useUpdateVendorMutation,
  usePatchVendorMutation,
  useGetAllOnboardingsQuery,
  useGetOnboardingQuery,
  useCreateOnboardingMutation,
  useExpireOnboardingMutation,
  useUpdateVendorAssignmentsMutation,
  useActivateVendorMutation,
  useDeactivateVendorMutation,
  useMarkVendorDormantMutation,
  useSendPaymentScheduleEmailMutation,
  useCreateProgramMutation,
  usePatchVendorProgramMutation,
  useActivateVendorProgramMutation,
  useDeactivateVendorProgramMutation,
  useDeleteVendorProgramMutation,
  useRejectVendorProgramPortalAccessMutation,
  useOfferVendorProgramPricingMutation,
  useGetVendorProgramPortalConfigurationsQuery,
  useCreateFactoringVendorProgramPromotionMutation,
  useCreateFactoringVendorProgramPromotionsMutation,
  useApproveVendorProgramPromotionMutation,
  useCreateProgramOverrideMutation,
  usePatchProgramOverrideMutation,
  useAddVendorAccountMutation,
  useVerifyVendorAccountMutation,
  useMakeVendorAccountPrimaryMutation,
  useMakeVendorAccountDebitOnlyMutation,
  useOverrideVendorAccountDirectDebitVerificationMutation,
  useRefreshVendorAccountUnderwritingTransactionsMutation,
  useHideSecondaryVendorAccountMutation,
  useRequestBankDocumentMutation,
  useGetUserAccountsQuery,
  useGetAllAbandonedUserAccountsQuery,
  useAddUserAccountMutation,
  useResendUserAccountInviteMutation,
  usePromoteUserAccountMutation,
  useToggleUserAccountReadOnlyMutation,
  useDeactivateUserAccountMutation,
  useReactivateUserAccountMutation,
  useDeleteUserAccountMutation,
  useGetVendorUserAccountActivityMetricsQuery,
  useGetVendorActivityMetricsQuery,
  useGetTaskSchedulesQuery,
  useGetAllTaskExecutionsQuery,
  useCreateTaskExecutionMutation,
  useCreateVendorServiceAgreementMutation,
  useGetVendorActivationStatisticsQuery,
  useLazyGetVendorActivationStatisticsQuery,
  useGetVendorServiceAgreementsQuery,
  useGetVendorServiceAgreementQuery,
  useDeleteVendorServiceAgreementMutation,
  useCreateVendorPartnerServiceAgreementMutation,
  useGetVendorPartnerServiceAgreementsQuery,
  useGetVendorPartnerServiceAgreementQuery,
  useDeleteVendorPartnerServiceAgreementMutation,
  useImpersonateVendorMutation,
  useEndVendorImpersonationMutation,
  useEndPartnerImpersonationMutation,
  useGetVendorDocumentsQuery,
  useGetAllVendorDocumentsQuery,
  useGetVendorDocumentQuery,
  useApproveVendorDocumentMutation,
  useUnapproveVendorDocumentMutation,
  useRejectVendorDocumentMutation,
  useHideVendorDocumentMutation,
  useGetVendorNotesQuery,
  useAddVendorNoteMutation,
  useGetVendorActionItemsQuery,
  useGetAllVendorActionItemsQuery,
  useGetVendorActionItemAssigneesQuery,
  useGetVendorActionItemTimelineQuery,
  useCreateVendorActionItemMutation,
  useUpdateVendorActionItemMutation,
  useCreateVendorActionItemNoteMutation,
  useGetVendorAuditQuery,
  useUploadFacilitatorsMutation,
  useGetAllFacilitatorsQuery,
  useGetFacilitatorTotalsQuery,
  useApproveFacilitatorsMutation,
  useRevokeFacilitatorsMutation,
  useUploadVendorDocumentMutation,
  useBulkApproveVendorDocumentsMutation,
  usePatchUserAccountMutation,
  useGetProgramsQuery,
  useGetProgramStatisticsQuery,
  useGetProgramOverridesQuery,
  useUpdateVendorDocumentContentsMutation,
  useGetProgramQuery,
  usePatchProgramMutation,
  usePatchVendorFeaturesMutation,
  useGetAllVendorSurveysQuery,
  useGetAllVendorThirdPartyBusinessVerificationsQuery,
  useMarkPartnerEnabledMutation,
  useGetVendorUnderwritingQuery,
  useUpdateVendorUnderwritingMutation,
  useGetVendorLimitAuditLogQuery,
  useGetAllVendorUnderwritingAssessmentsQuery,
  useGetVendorUnderwritingAssessmentQuery,
  useCreateVendorUnderwritingAssessmentMutation,
  useApproveVendorUnderwritingAssessmentMutation,
  useRejectVendorUnderwritingAssessmentMutation,
  useExpireVendorUnderwritingAssessmentMutation,
  useCloneVendorUnderwritingAssessmentMutation,
  useUpdateVendorUnderwritingAssessmentMutation,
  useGetVendorAccountUnderwritingBalanceQuery,
  useGetVendorAccountUnderwritingTransactionsQuery,
  useGetVendorAccountUnderwritingTransactionQuery,
  useGetUnderwritingTransactionLabellingDetailsQuery,
  useGetUnderwritingTransactionLabellingDetailQuery,
  usePatchUnderwritingTransactionLabellingMutation,
  useGetVendorAccountUnderwritingSummaryTimeSeriesQuery,
  useGetVendorAccountUnderwritingCounterpartySummariesQuery,
  useCreateVendorAccountUnderwritingTransactionMatchedInvoicesMutation,
  useGetVendorAccountUnderwritingTransactionMatchedInvoicesQuery,
  useDeleteVendorAccountUnderwritingTransactionMatchedInvoicesMutation,
  useSuggestVendorAccountUnderwritingTransactionMatchedInvoicesMutation,
  useSuggestVendorMatchedInvoicesQuery,
  useGlobalSearchQuery,
  useUpdateVendorAccountUnderwritingMutation,
  useGetAllVendorEventsQuery,
  useGetAllVendorEmailSequencesQuery,
  useGetAllVendorEmailSequencesAcrossVendorsQuery,
  useBulkVendorEmailSequenceActionMutation,
  usePauseVendorEmailSequenceMutation,
  useResumeVendorEmailSequenceMutation,
  useActivateVendorEmailSequenceMutation,
  useDeactivateVendorEmailSequenceMutation,
  useCloneVendorEmailSequenceMutation,
  useCreateVendorEmailSequenceFromTemplateMutation,
  useSendVendorEmailSequenceStepNowMutation,
  useGetVendorEmailSequenceQuery,
  useGetVendorEmailSequenceConditionEvaluationsQuery,
  useSaveVendorEmailSequenceMutation,
  useGetAllConfigurationSettingsQuery,
  useUpdateConfigurationSettingMutation,
  useRequestSqlQueryAnswerMutation,
  useGetUnderwritingLabelMetricsCoverageQuery,
  useGetVendorAccountUnderwritingDashboardQuery,
  useGetVendorAccountUnderwritingCashFlowCategoriesQuery,
  useGetVendorAccountUnderwritingCashFlowSubcategoriesQuery,
  useGetVendorAccountUnderwritingTransactionMetadataQuery,
  useImportVendorAccountTransactionsMutation,
  usePutVendorAccountTargetsMutation,
  useGetVendorUccFilingsQuery,
  useCreateVendorUccFilingMutation,
  useUpdateVendorUccFilingMutation,
  useGetAllUccFilingsQuery,
  useGetAllVendorSubAccountsQuery,
  useGetNextVendorSubAccountValueQuery,
  useCreateVendorSubAccountMutation,
  useUpdateVendorSubAccountMutation,
  useDeleteVendorSubAccountMutation,
  useGetAllVendorProgramPromotionsQuery,
  useGetVendorProgramPromotionQuery,
  useCreateVendorProgramPromotionsMutation,
  useCreateMultiPartnerUpsellVendorProgramPromotionsMutation,
  useUploadVendorProgramPromotionsMutation,
  usePatchVendorProgramPromotionMutation,
  useOverrideVendorProgramPromotionSelectedInvoiceMutation,
  useClearVendorProgramPromotionSelectedInvoiceOverrideMutation,
  useGetProgramPromotionsQuery,
  usePutProgramPromotionMutation,
  useDeleteProgramPromotionMutation,
  useGetPartnerStatusSummaryQuery,
  useGetVendorStatusSummaryQuery,
  useGetVendorUpsellStatusSummaryQuery,
  useGetVendorStatusSummaryByIdQuery,
} = vendorsApi;

export const exportAllVendors = async (filterParams?: types.ExportVendorFilterParams, exportFormat: string = 'default'): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.vendorUrl}/vendors/csv/export`);
  appendFilterParams(url, filterParams);
  appendFilterParams(url, { exportFormat });

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportAllVendorDocuments = async (filterParams?: types.VendorDocumentFilterParams): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.vendorUrl}/vendors/documents/all/csv/export`);
  appendFilterParams(url, filterParams);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export type ExportFilterParams = types.IPageFilterParams & types.ISortFilterParams & { readonly columns?: string | readonly string[] };

export const exportAllAbandonedUsers = async (filterParams?: ExportFilterParams): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.vendorUrl}/abandoned-user-accounts/csv/export`);
  appendFilterParams(url, filterParams);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportAllVendorSurveys = async (filterParams?: ExportFilterParams): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.vendorUrl}/vendor-surveys/csv/export`);
  appendFilterParams(url, filterParams);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const downloadVendorDocument = async (vendorId: types.UUID, documentId: types.UUID): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.vendorUrl}/vendors/${vendorId}/documents/${documentId}/download`);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  if (response.status !== 200) {
    const error = await response.json();
    throw new Error(error?.message || response.statusText);
  }

  return await response.blob();
};

export const exportAllVendorAccountUnderwritingTransactions = async (params: { vendorId: api.UUID; vendorAccountId: api.UUID; } & types.VendorAccountUnderwritingTransactionFilterParams): Promise<BlobPart> => {
  const idToken = await getIdToken();
  const { vendorId, vendorAccountId, ...filterParams } = params;

  const url = new URL(`${CONFIG.api.vendorUrl}/vendors/${vendorId}/accounts/${vendorAccountId}/underwriting/transactions/csv/export`);
  appendFilterParams(url, filterParams);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportUnderwritingTransactionLabellingDetailsCsv = async (params?: types.VendorAccountUnderwritingTransactionLabellingDetailsFilterParams): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.vendorUrl}/underwriting/transactions/labelling-details/csv/export`);
  appendFilterParams(url, params);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  if (response.status !== 200) {
    const error = await response.json();
    throw new Error(error?.message || response.statusText);
  }

  return await response.blob();
};

export const exportVendorProgramPromotions = async (filterParams?: types.VendorProgramPromotionFilterParams): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.vendorUrl}/vendor-program-promotions/csv/export`);
  appendFilterParams(url, filterParams);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  if (response.status !== 200) {
    const error = await response.json();
    throw new Error(error?.message || response.statusText);
  }

  return await response.blob();
};

/**
 * `exportFormat: 'enablement'` exports the vendors "Enablement" CSV (supplier IDs, program flag, locale)
 * for exactly the vendors these filters select; the default is the vendor status summary CSV.
 */
export const exportVendorStatusSummary = async (
  filterParams?: api.VendorStatusSummaryFilterParams,
  exportFormat?: 'enablement',
): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.vendorUrl}/enablement/vendorStatusSummary/csv/export`);
  appendFilterParams(url, filterParams);
  appendFilterParams(url, { exportFormat });

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportUpsellStatusSummary = async (
  filterParams?: api.VendorUpsellStatusSummaryFilterParams,
): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.vendorUrl}/enablement/upsellStatusSummary/csv/export`);
  appendFilterParams(url, filterParams);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportAllVendorSubAccounts = async (vendorId?: string, searchTerm?: string): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.vendorUrl}/vendor-sub-accounts/export/csv`);
  if (vendorId) {
    appendFilterParams(url, { vendorId });
  }
  if (searchTerm) {
    appendFilterParams(url, { searchTerm });
  }

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportPartnerStatusSummary = async (): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.vendorUrl}/enablement/partnerStatusSummary/csv/export`);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};
